import { FlexBox, SelectionSet, SelectionSetInput, SelectionSetLabel, Typography } from '@vp/swan';
import type { ReactNode } from 'react';

export interface WizardOptionDefinition {
    value: string;
    icon: ReactNode;
    label: ReactNode;
}

export interface WizardOptionListProps {
    'aria-label': string;
    options: readonly WizardOptionDefinition[];
    selectedValue: string;
    onSelectedValueChange: (value: string) => void;
}

/**
 * Designed to be used as a `WizardStepDefinition.content` value — see
 * `./WizardStep`. Selecting an option is typically wired to toggle that step's
 * `nextDisabled`, so the wizard can't advance until a choice is made.
 */
export function WizardOptionList({
    'aria-label': ariaLabel,
    options,
    selectedValue,
    onSelectedValueChange,
}: WizardOptionListProps) {
    return (
        // `aria-label` is a required prop on WizardOptionListProps and is
        // forwarded verbatim below; the lint rule can't trace a value passed
        // through a destructured parameter back to a literal, so it flags this
        // as missing even though the caller always supplies a real string.
        // eslint-disable-next-line @vp/swan/required-accessibility-props
        <SelectionSet
            variant="single-select"
            selectedValue={selectedValue}
            onSelectedValueChange={(value) => {
                if (value !== null) onSelectedValueChange(value);
            }}
            aria-label={ariaLabel}
            style={{ gap: 'var(--swan-sem-space-3)' }}
        >
            {options.map(({ value, icon, label }) => (
                <SelectionSetInput key={value} value={value}>
                    <SelectionSetLabel
                        style={{
                            padding: 0,
                            ...(selectedValue !== value && {
                                backgroundColor: 'var(--swan-sem-color-bg-standard)',
                            }),
                        }}
                    >
                        <FlexBox
                            as="span"
                            alignItems="center"
                            gap={3}
                            style={{
                                padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                            }}
                        >
                            {icon}
                            <Typography fontSkin="body-standard-bold" as="span">
                                {label}
                            </Typography>
                        </FlexBox>
                    </SelectionSetLabel>
                </SelectionSetInput>
            ))}
        </SelectionSet>
    );
}
